import { useEffect } from 'react'
import { toast } from 'react-hot-toast'

import { useGetCurrenciesCatalogs } from '@/domain/currencies'
import { CurrencyCatalogsDTO } from '@/domain/currencies'
import { useUpdateWallet } from '@/domain/wallet'
import { useAppPreferencesStore, useUserStore } from '@/store'

/**
 * Hook for loading and managing the display currency
 * Equivalent of useLoadDisplayCurrency from the React Native version
 */
export const useLoadDisplayCurrency = () => {
  const { mutateAsync: updateWallet } = useUpdateWallet()

  // Load display currencies immediately, unconditionally
  const {
    data: displayCurrencies,
    isLoading,
    isError,
  } = useGetCurrenciesCatalogs({
    is_available: true,
    is_display_currency: true,
  })

  const availableDisplayCurrencies = (displayCurrencies as any)
    ?.currencies as CurrencyCatalogsDTO[]

  const { displayCurrency, setDisplayCurrency } = useAppPreferencesStore()
  const { wallet } = useUserStore()
  const walletId = wallet?.walletId
  const userDisplayCurrency = wallet?.displayCurrency

  const handleSelectDisplayCurrency = async (displayCurrencyCode: string) => {
    try {
      // Find the selected currency in the available list
      const selectedCurrency = availableDisplayCurrencies?.find(
        (currency) => currency.isin === displayCurrencyCode,
      )

      if (!selectedCurrency) {
        throw new Error('Currency not found')
      }

      // First update local state
      setDisplayCurrency(selectedCurrency)

      // Update on the server
      if (walletId) {
        await updateWallet({
          wallet_id: walletId,
          display_fiat_currency: displayCurrencyCode,
        })
      }
    } catch (error) {
      console.error('Error updating wallet currency:', error)
      toast.error('Failed to update display currency')
    }
  }

  // Set default currency on first launch
  useEffect(() => {
    if (isError || !availableDisplayCurrencies?.length) return

    // If no currency is selected, set USD as default
    if (!displayCurrency) {
      const defaultDisplayCurrency = availableDisplayCurrencies.find(
        (currency) => currency.isin === 'USD',
      )

      if (defaultDisplayCurrency) {
        setDisplayCurrency(defaultDisplayCurrency)
      }
    }
  }, [availableDisplayCurrencies, displayCurrency, isError])

  // Sync wallet currency with local state
  useEffect(() => {
    if (isError || !availableDisplayCurrencies?.length) return

    if (userDisplayCurrency && availableDisplayCurrencies.length) {
      const userSelectedDisplayCurrency = availableDisplayCurrencies.find(
        (currency) => currency.isin === userDisplayCurrency,
      )

      if (
        userSelectedDisplayCurrency &&
        userSelectedDisplayCurrency.isin !== displayCurrency?.isin
      ) {
        setDisplayCurrency(userSelectedDisplayCurrency)
      }
    }
  }, [
    availableDisplayCurrencies,
    userDisplayCurrency,
    displayCurrency,
    isError,
  ])

  return {
    displayCurrencies: availableDisplayCurrencies || [],
    isLoading,
    isError,
    handleSelectDisplayCurrency,
  }
}
